// The forge at night: furnace, smith, anvil and the ingot staircase (the bonding curve).
//
// Everything that moves is driven by the inputs, which are always real launch state:
//   flame colour/height  = temper colour of the current effective difficulty
//   flame dim + flicker  = idle decay
//   hammer tempo         = log(hashrate): your miner's on the token page, the launch's estimated
//                          network hashrate on the landing page; each strike throws a few sparks
//   ingot hop            = a Forged event (lot index + the difficulty it met)
//   white flash + burst  = your proof found
//   cold furnace         = scheduled launch not ignited
//   pour into the pool   = graduation
import { N_LOTS } from '@hashcurve/protocol';
import { lotHeight } from '../lib/lots';
import { temperAt, temperT } from '../lib/temper';
import { Atlas, dither, line, mix, noise } from './engine';
import { drawText, textWidth } from './font';
import { SCENE_PAL, SCENE_SPRITES } from './sprites';

export type SceneMode = 'cold' | 'active' | 'graduated';

export interface SceneLot {
  /** 0..1 of the lot in circulation */
  fill: number;
  /** temper colour of the difficulty it was forged at; null = empty or history not loaded */
  color: string | null;
}

export interface SceneInputs {
  mode: SceneMode;
  /** current effective difficulty (expected hashes); null = unknown */
  difficulty: number | null;
  /** 0 = within the grace period, rising to 1 as difficulty decays to its floor */
  decay: number;
  /** your miner's hashrate in H/s; 0 = your miner is not hashing */
  hashrate: number;
  /** you hold a valid proof that has not been used yet */
  holding: boolean;
  lots: readonly SceneLot[];
  /** index of the next lot to be forged, -1 when none */
  nextLot: number;
}

export const EMPTY_LOTS: readonly SceneLot[] = Array.from({ length: N_LOTS }, () => ({ fill: 0, color: null }));

const C = {
  k: '#05040A',
  sky: '#0D0F2A',
  skyHi: '#171A3E',
  wall: '#15111E',
  wall2: '#18131F',
  wall3: '#120E19',
  mortar: '#0C0A12',
  floor: '#1B1627',
  floorHi: '#2E2742',
  floorJoint: '#120F1B',
  brick: '#5E3342',
  brick2: '#6B3A4A',
  brickHi: '#8A4E5E',
  brickMortar: '#2E1822',
  arch: '#7A4454',
  mouth: '#0B0508',
  stone: '#3B3358',
  stoneHi: '#6A5C94',
  white: '#FFF6D8',
  molten: '#FFC247',
  ember: '#FF7A2F',
  deep: '#B8441C',
  quench: '#7FE0FF',
  dot: '#4A4068',
  unknown: '#6E6488',
  skin: '#E3AC7E',
  wood: '#8C5A34',
  iron: '#4A4560',
  ironHi: '#6E6886',
  steel: '#A9A2C0',
  crucible: '#2C2840',
};

interface Layout {
  W: number;
  H: number;
  floorY: number;
  fx: number;
  fw: number;
  ftop: number;
  cx: number;
  cw: number;
  mx: number;
  mw: number;
  mtop: number;
  mbot: number;
  sx: number;
  sy: number;
  stx: number;
  sty: number;
  ax: number;
  ay: number;
  rackX: number;
  rackW: number;
  rackMaxH: number;
  rackBase: number;
  winX: number;
  winY: number;
  winW: number;
  winH: number;
}

function layout(W: number, H: number): Layout {
  const floorY = H - 14;
  const fx = 4;
  const fw = 46;
  const fh = Math.min(58, floorY - 12);
  const ftop = floorY - fh;
  const mw = 26;
  const mbot = floorY - 5;
  const mh = Math.min(24, fh - 18);
  const sx = fx + fw + 3;
  const stx = sx + 18;
  const sty = floorY - 3;
  const ax = stx - 4;
  const ay = sty - 10;
  const rackX = ax + 24 + 9;
  const winW = 26;
  return {
    W,
    H,
    floorY,
    fx,
    fw,
    ftop,
    cx: fx + 15,
    cw: 16,
    mx: fx + 10,
    mw,
    mtop: mbot - mh,
    mbot,
    sx,
    sy: floorY - 30,
    stx,
    sty,
    ax,
    ay,
    rackX,
    rackW: Math.max(24, W - 6 - rackX),
    rackMaxH: Math.min(H >= 108 ? 60 : 48, floorY - 24),
    rackBase: floorY - 2,
    winX: Math.max(rackX + 4, W - 6 - winW - Math.round((W - rackX) * 0.25)),
    winY: 6,
    winW,
    winH: 16,
  };
}

let atlas: Atlas | null = null;
/** baked backgrounds by size and mode (a scene switches between at most three) */
const bgCache = new Map<string, HTMLCanvasElement>();
const sprites = () => (atlas ??= new Atlas(SCENE_SPRITES, SCENE_PAL));

// ------------------------------------------------------------------ static background
function bakeBackground(L: Layout, mode: SceneMode): HTMLCanvasElement {
  const cv = document.createElement('canvas');
  cv.width = L.W;
  cv.height = L.H;
  const ctx = cv.getContext('2d')!;
  const A = sprites();

  // back wall: staggered dark bricks, darker toward the ceiling
  ctx.fillStyle = C.mortar;
  ctx.fillRect(0, 0, L.W, L.floorY);
  for (let row = 0; row * 6 < L.floorY; row++) {
    const off = row % 2 ? 6 : 0;
    for (let x = -off; x < L.W; x += 12) {
      const n = noise(x, row, 3);
      ctx.fillStyle = n < 0.33 ? C.wall : n < 0.66 ? C.wall2 : C.wall3;
      ctx.fillRect(x + 1, row * 6 + 1, 11, 5);
    }
  }
  ctx.fillStyle = C.k;
  dither(ctx, 0, 0, L.W, 3);

  // window onto the night
  const { winX: wx, winY: wy, winW: ww, winH: wh } = L;
  ctx.fillStyle = C.k;
  ctx.fillRect(wx - 2, wy - 2, ww + 4, wh + 4);
  ctx.fillStyle = C.stone;
  ctx.fillRect(wx - 1, wy - 1, ww + 2, wh + 2);
  ctx.fillStyle = C.sky;
  ctx.fillRect(wx, wy, ww, wh);
  ctx.fillStyle = C.skyHi;
  dither(ctx, wx, wy + wh - 5, ww, 5);
  // moon (a crescent)
  ctx.fillStyle = '#F2EAD8';
  ctx.fillRect(wx + ww - 9, wy + 3, 3, 1);
  ctx.fillRect(wx + ww - 10, wy + 4, 2, 3);
  ctx.fillRect(wx + ww - 9, wy + 7, 3, 1);
  ctx.fillStyle = C.stone;
  ctx.fillRect(wx + ww / 2 - 1, wy, 2, wh);
  ctx.fillRect(wx, wy + wh / 2 - 1, ww, 2);
  ctx.fillStyle = C.stoneHi;
  ctx.fillRect(wx - 2, wy + wh + 1, ww + 4, 1);

  // tools on the wall
  const tx = L.sx + 1;
  if (tx + 22 < L.winX) {
    A.draw(ctx, 'peg', tx + 2, 7);
    A.draw(ctx, 'horseshoe', tx, 9);
    A.draw(ctx, 'peg', tx + 13, 5);
    A.draw(ctx, 'tongs', tx + 11, 7);
  }

  // floor
  ctx.fillStyle = C.floor;
  ctx.fillRect(0, L.floorY, L.W, L.H - L.floorY);
  ctx.fillStyle = C.floorHi;
  ctx.fillRect(0, L.floorY, L.W, 1);
  ctx.fillStyle = C.floorJoint;
  for (let x = 7; x < L.W; x += 18) ctx.fillRect(x, L.floorY + 1, 1, 5);
  for (let x = 16; x < L.W; x += 18) ctx.fillRect(x, L.floorY + 7, 1, L.H - L.floorY - 7);
  ctx.fillRect(0, L.floorY + 6, L.W, 1);
  ctx.fillStyle = C.k;
  dither(ctx, 0, L.H - 3, L.W, 3, 1);

  // chimney
  const { cx, cw } = L;
  ctx.fillStyle = C.k;
  ctx.fillRect(cx - 1, 0, cw + 2, L.ftop);
  bricks(ctx, cx, 0, cw, L.ftop, 4);

  // furnace body
  const { fx, fw, ftop, floorY } = L;
  ctx.fillStyle = C.k;
  ctx.fillRect(fx - 1, ftop - 1, fw + 2, floorY - ftop + 2);
  bricks(ctx, fx, ftop + 3, fw, floorY - ftop - 3, 0);
  // cap stone
  ctx.fillStyle = C.stone;
  ctx.fillRect(fx - 2, ftop - 1, fw + 4, 4);
  ctx.fillStyle = C.stoneHi;
  ctx.fillRect(fx - 2, ftop - 1, fw + 4, 1);
  ctx.fillStyle = C.k;
  ctx.fillRect(fx - 2, ftop + 3, fw + 4, 1);

  // mouth: an arched opening with a ring of lighter bricks and a stone sill
  const { mx, mw, mtop, mbot } = L;
  archFill(ctx, mx - 2, mtop - 2, mw + 4, mbot - mtop + 2, C.k);
  archFill(ctx, mx - 1, mtop - 1, mw + 2, mbot - mtop + 1, C.arch);
  archFill(ctx, mx, mtop, mw, mbot - mtop, mode === 'cold' ? '#07070C' : C.mouth);
  ctx.fillStyle = C.k;
  ctx.fillRect(mx - 3, mbot, mw + 6, 4);
  ctx.fillStyle = C.stone;
  ctx.fillRect(mx - 2, mbot, mw + 4, 3);
  ctx.fillStyle = C.stoneHi;
  ctx.fillRect(mx - 2, mbot, mw + 4, 1);
  // grate bars
  ctx.fillStyle = '#1E1A30';
  for (let x = mx + 2; x < mx + mw - 1; x += 4) ctx.fillRect(x, mbot - 2, 2, 2);

  // anvil on its stump (not after graduation: mining has ended)
  if (mode !== 'graduated') {
    A.draw(ctx, 'stump', L.stx, L.sty);
    A.draw(ctx, 'anvil', L.ax, L.ay);
  }

  // a plaque over the low end of the staircase
  if (L.rackW >= 110) {
    const label = 'THE CURVE';
    const pw = textWidth(label) + 6;
    const px = L.rackX + 2;
    const py = L.rackBase - Math.round(L.rackMaxH * 0.55) - 4;
    ctx.fillStyle = C.k;
    ctx.fillRect(px - 1, py - 1, pw + 2, 11);
    ctx.fillStyle = '#3E2414';
    ctx.fillRect(px, py, pw, 9);
    ctx.fillStyle = '#6A3F24';
    ctx.fillRect(px, py, pw, 1);
    ctx.fillStyle = '#C9A86A';
    drawText(ctx, label, px + 3, py + 2);
    ctx.fillStyle = C.k;
    ctx.fillRect(px + 3, py + 9, 1, 3);
    ctx.fillRect(px + pw - 4, py + 9, 1, 3);
  }

  // stone plinth under the ingot staircase
  ctx.fillStyle = C.k;
  ctx.fillRect(L.rackX - 2, L.rackBase, L.rackW + 4, 3);
  ctx.fillStyle = C.stone;
  ctx.fillRect(L.rackX - 1, L.rackBase + 1, L.rackW + 2, 1);
  return cv;
}

function bricks(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, seed: number) {
  ctx.fillStyle = C.brickMortar;
  ctx.fillRect(x, y, w, h);
  for (let r = 0; r * 4 < h; r++) {
    const off = r % 2 ? 4 : 0;
    for (let bx = x - off; bx < x + w; bx += 8) {
      const x0 = Math.max(x, bx + 1);
      const x1 = Math.min(x + w, bx + 8);
      if (x1 <= x0) continue;
      const n = noise(bx, r, seed + 11);
      ctx.fillStyle = n < 0.5 ? C.brick : C.brick2;
      const h0 = Math.min(3, y + h - (y + r * 4 + 1));
      if (h0 <= 0) continue;
      ctx.fillRect(x0, y + r * 4 + 1, x1 - x0, h0);
      if (n > 0.8) {
        ctx.fillStyle = C.brickHi;
        ctx.fillRect(x0, y + r * 4 + 1, x1 - x0, 1);
      }
    }
  }
}

/** a rectangle whose top corners are stepped into an arch */
function archFill(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string) {
  ctx.fillStyle = color;
  const inset = [5, 3, 2, 1, 1];
  for (let r = 0; r < h; r++) {
    const i = inset[r] ?? 0;
    ctx.fillRect(x + i, y + r, w - 2 * i, 1);
  }
}

// ------------------------------------------------------------------ runtime
type Pose = 'rest' | 'raise' | 'mid' | 'strike';

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

interface Flight {
  lot: number;
  color: string;
  f: number;
}

const FLIGHT_SIT = 4;
const FLIGHT_LEN = 9;
const COOL_FRAMES = 12;
const POUR_FRAMES = 84;

export class ForgeRuntime {
  inputs: SceneInputs = { mode: 'active', difficulty: null, decay: 0, hashrate: 0, holding: false, lots: EMPTY_LOTS, nextLot: 0 };
  private L: Layout;
  private bg: HTMLCanvasElement | null = null;
  private bgKey = '';
  private sparks: Spark[] = [];
  private flights: Flight[] = [];
  private cooling = new Map<number, { color: string; left: number }>();
  private flash = 0;
  private bigStrike = 0;
  private cycle = 0;
  private pour = -1;
  private lastMode: SceneMode | null = null;
  private seed = 1;

  constructor(W: number, H: number) {
    this.L = layout(W, H);
  }

  get width() {
    return this.L.W;
  }

  resize(W: number, H: number) {
    if (W === this.L.W && H === this.L.H) return;
    this.L = layout(W, H);
    this.bg = null;
  }

  /** where the strike lands (logical px) */
  get strikePoint() {
    return { x: this.L.ax + 10, y: this.L.ay - 1 };
  }

  /** a lot was forged (a real Forged event) */
  forged(lot: number, color: string, reduced = false) {
    if (lot < 0 || lot >= N_LOTS) return;
    if (reduced) return;
    this.flights = this.flights.filter((f) => f.lot !== lot);
    this.flights.push({ lot, color, f: 0 });
    if (this.flights.length > 6) this.flights.shift();
  }

  /** your proof was found */
  proof(reduced = false) {
    if (reduced) return;
    this.flash = 4;
    this.bigStrike = 4;
    const p = this.strikePoint;
    this.burst(p.x, p.y, 28, 1.6);
  }

  /** start (or skip) the graduation pour */
  setPour(done: boolean) {
    this.pour = done ? POUR_FRAMES : 0;
    this.lastMode = this.inputs.mode;
  }

  private rand() {
    this.seed = (this.seed * 1103515245 + 12345) & 0x7fffffff;
    return this.seed / 0x7fffffff;
  }

  private burst(x: number, y: number, n: number, power = 1) {
    for (let i = 0; i < n; i++) {
      const a = Math.PI + this.rand() * Math.PI;
      const v = (0.8 + this.rand() * 1.8) * power;
      this.sparks.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 0.6, life: 5 + Math.floor(this.rand() * 7) });
    }
    if (this.sparks.length > 80) this.sparks.splice(0, this.sparks.length - 80);
  }

  private column(i: number) {
    const L = this.L;
    const x0 = L.rackX + Math.floor((i * L.rackW) / N_LOTS);
    const x1 = L.rackX + Math.floor(((i + 1) * L.rackW) / N_LOTS);
    const h = Math.max(2, Math.round(L.rackMaxH * lotHeight(i)));
    return { x: x0, w: Math.max(1, x1 - x0), h, top: L.rackBase - h };
  }

  /** hammer period in frames from the hashrate (log scale); 0 = not hashing */
  private period(): number {
    const hr = this.inputs.hashrate;
    if (!(hr > 0)) return 0;
    const sps = Math.min(3, Math.max(0.35, 0.4 + 0.3 * Math.log10(Math.max(1, hr) / 1000)));
    return Math.max(4, Math.min(20, Math.round(12 / sps)));
  }

  /** advance one stepped frame */
  step(frame: number) {
    const inp = this.inputs;
    if (inp.mode !== this.lastMode) {
      // entering graduation (or loading a graduated launch) plays the pour once
      if (inp.mode === 'graduated' && this.pour < 0) this.pour = 0;
      this.lastMode = inp.mode;
    }
    if (inp.mode === 'graduated' && this.pour >= 0 && this.pour < POUR_FRAMES) this.pour++;

    // hammering
    const P = this.period();
    if (this.bigStrike > 0) this.bigStrike--;
    if (P && inp.mode === 'active' && !inp.holding) {
      this.cycle = (this.cycle + 1) % P;
      if (this.cycle === 0) {
        const p = this.strikePoint;
        this.burst(p.x, p.y, 3);
      }
    } else this.cycle = 2;

    // furnace embers
    if (inp.mode === 'active' && inp.difficulty !== null && frame % 3 === 0 && this.rand() < 0.6 - inp.decay * 0.4) {
      const L = this.L;
      this.sparks.push({ x: L.mx + 4 + this.rand() * (L.mw - 8), y: L.mtop + 4, vx: (this.rand() - 0.5) * 0.4, vy: -0.6 - this.rand() * 0.5, life: 6 + Math.floor(this.rand() * 6) });
    }

    for (const s of this.sparks) {
      s.x += s.vx;
      s.y += s.vy;
      s.vy += s.vy < 0 && s.life > 8 ? 0.05 : 0.3;
      s.life--;
    }
    this.sparks = this.sparks.filter((s) => s.life > 0 && s.y < this.L.floorY);

    for (const f of this.flights) f.f++;
    for (const f of this.flights) if (f.f === FLIGHT_SIT + FLIGHT_LEN) this.cooling.set(f.lot, { color: f.color, left: COOL_FRAMES });
    this.flights = this.flights.filter((f) => f.f < FLIGHT_SIT + FLIGHT_LEN);
    for (const [k, v] of this.cooling) {
      v.left--;
      if (v.left <= 0) this.cooling.delete(k);
    }
    if (this.flash > 0) this.flash--;
  }

  private pose(): Pose {
    const inp = this.inputs;
    if (this.bigStrike > 0) return this.bigStrike > 2 ? 'strike' : 'mid';
    if (inp.mode !== 'active') return 'rest';
    if (inp.holding) return 'raise';
    const P = this.period();
    if (!P) return 'rest';
    const c = this.cycle;
    if (c === 0) return 'strike';
    if (c === 1 || c === P - 1) return 'mid';
    return 'raise';
  }

  draw(ctx: CanvasRenderingContext2D, frame: number) {
    const L = this.L;
    const inp = this.inputs;
    const key = `${L.W}x${L.H}:${inp.mode}`;
    if (!this.bg || this.bgKey !== key) {
      let bg = bgCache.get(key);
      if (!bg) {
        if (bgCache.size > 12) bgCache.clear();
        bg = bakeBackground(L, inp.mode);
        bgCache.set(key, bg);
      }
      this.bg = bg;
      this.bgKey = key;
    }
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(this.bg, 0, 0);
    this.drawStars(ctx, frame);
    this.drawFire(ctx, frame);
    this.drawRack(ctx, frame);
    if (inp.mode === 'graduated') this.drawPour(ctx, frame);
    this.drawSmith(ctx, frame);
    this.drawFlights(ctx, frame);
    for (const s of this.sparks) {
      ctx.fillStyle = s.life > 7 ? C.white : s.life > 4 ? C.molten : C.ember;
      ctx.fillRect(Math.round(s.x), Math.round(s.y), 1, 1);
    }
    if (this.flash > 0) {
      ctx.fillStyle = C.white;
      if (this.flash >= 3) ctx.fillRect(0, 0, L.W, L.H);
      else dither(ctx, 0, 0, L.W, L.H, this.flash);
    }
  }

  private drawStars(ctx: CanvasRenderingContext2D, frame: number) {
    const { winX, winY, winW } = this.L;
    const stars = [
      [3, 3],
      [8, 10],
      [4, 12],
      [winW - 4, 12],
      [9, 2],
    ];
    stars.forEach(([x, y], i) => {
      const n = noise(i, Math.floor(frame / 5));
      ctx.fillStyle = n > 0.75 ? '#FFF6D8' : n > 0.3 ? '#A99EC0' : '#3B3358';
      ctx.fillRect(winX + x!, winY + y!, 1, 1);
    });
  }

  private flameColors() {
    const inp = this.inputs;
    const d = inp.difficulty ?? 1;
    const base = temperAt(temperT(d));
    const dim = (c: string) => mix(c, C.mouth, inp.decay * 0.55);
    return { base, core: dim(C.white), light: dim(mix(base, C.white, 0.5)), mid: dim(base), tip: dim(mix(base, C.deep, 0.35)), heat: 0.5 + 0.5 * temperT(d) };
  }

  private drawFire(ctx: CanvasRenderingContext2D, frame: number) {
    const L = this.L;
    const inp = this.inputs;
    const { mx, mw, mtop, mbot } = L;
    const inner = mw - 2;
    if (inp.mode === 'cold' || inp.difficulty === null) {
      // a cold furnace: grey ash, one or two dull coals
      for (let c = 0; c < inner; c++) {
        const n = noise(c, 5);
        ctx.fillStyle = n > 0.85 ? '#4A1E1A' : n > 0.4 ? '#221D2E' : '#16131F';
        ctx.fillRect(mx + 1 + c, mbot - 3, 1, 1);
      }
      return;
    }
    const col = this.flameColors();
    const decay = inp.decay;
    const mh = mbot - mtop - 3;
    // an idle furnace flickers: whole-flame dips, more often as decay deepens
    const dip = decay > 0 && noise(frame, 77) < 0.12 + decay * 0.45 ? 0.45 + noise(frame, 78) * 0.3 : 1;
    const graduated = inp.mode === 'graduated';
    const heat = graduated ? 0.35 : col.heat;
    for (let c = 0; c < inner; c++) {
      const n = (noise(c, frame) + noise(c + 1, frame) + noise(c, frame + 1)) / 3;
      const u = (c - (inner - 1) / 2) / ((inner - 1) / 2);
      const shape = 1 - u * u * 0.55;
      const fh = Math.max(1, Math.round(mh * heat * shape * (0.55 + 0.5 * n) * dip));
      const x = mx + 1 + c;
      const yb = mbot - 3;
      const seg = (from: number, to: number, color: string) => {
        const a = Math.round(fh * from);
        const b = Math.round(fh * to);
        if (b <= a) return;
        ctx.fillStyle = color;
        ctx.fillRect(x, yb - b + 1, 1, b - a);
      };
      seg(0, 0.3, col.core);
      seg(0.3, 0.55, col.light);
      seg(0.55, 0.85, col.mid);
      seg(0.85, 1, col.tip);
      // licks above the arch get clipped by the arch shape
    }
    // arch mask (re-draw the arch corners over the flame tips)
    ctx.fillStyle = C.arch;
    const inset = [5, 3, 2, 1, 1];
    for (let r = 0; r < inset.length; r++) {
      const i = inset[r]!;
      ctx.fillRect(mx, mtop + r, i, 1);
      ctx.fillRect(mx + mw - i, mtop + r, i, 1);
    }
    // coals
    for (let c = 0; c < inner; c++) {
      const n = noise(c, Math.floor(frame / 2), 9);
      ctx.fillStyle = n > 0.7 ? C.molten : n > 0.3 ? C.ember : C.deep;
      ctx.fillRect(mx + 1 + c, mbot - 2, 1, 2);
    }
    // light pool on the floor
    ctx.fillStyle = mix(C.floor, col.base, 0.45 * (1 - decay * 0.6));
    const spread = Math.round(4 + 8 * heat);
    dither(ctx, mx - spread, L.floorY + 1, mw + spread * 2, 2, 0);
    ctx.fillStyle = mix(C.floor, col.base, 0.25 * (1 - decay * 0.6));
    dither(ctx, mx - spread + 3, L.floorY + 3, mw + spread * 2 - 6, 2, 1);
  }

  private drawRack(ctx: CanvasRenderingContext2D, frame: number) {
    const inp = this.inputs;
    const inFlight = new Set(this.flights.map((f) => f.lot));
    for (let i = 0; i < N_LOTS; i++) {
      const col = this.column(i);
      const lot = inp.lots[i];
      const cool = this.cooling.get(i);
      if (cool) {
        const c = mix(C.white, cool.color, 1 - cool.left / COOL_FRAMES);
        ingotColumn(ctx, col.x, col.top, col.w, col.h, c);
        continue;
      }
      if (lot && lot.fill > 0 && !inFlight.has(i)) {
        const h = Math.max(1, Math.round(col.h * lot.fill));
        if (lot.color) ingotColumn(ctx, col.x, col.top + (col.h - h), col.w, h, lot.color);
        else {
          ctx.fillStyle = C.unknown;
          ctx.fillRect(col.x, col.top + (col.h - h), col.w, h);
        }
      } else if ((col.x & 1) === 0) {
        ctx.fillStyle = C.dot;
        ctx.fillRect(col.x, col.top, 1, 1);
        if (i === 0 || i === N_LOTS - 1) {
          for (let y = col.top + 2; y < this.L.rackBase; y += 2) ctx.fillRect(col.x, y, 1, 1);
        }
      }
    }
    // live edge: a blinking white-hot caret over the next lot
    if (inp.mode === 'active' && inp.nextLot >= 0 && inp.nextLot < N_LOTS && Math.floor(frame / 4) % 2 === 0) {
      const col = this.column(inp.nextLot);
      ctx.fillStyle = C.white;
      ctx.fillRect(col.x - 1, col.top - 4, 3, 1);
      ctx.fillRect(col.x, col.top - 3, 1, 1);
      ctx.fillStyle = mix(C.white, C.mouth, 0.3);
      for (let y = col.top; y < this.L.rackBase; y += 2) ctx.fillRect(col.x, y, 1, 1);
    }
  }

  private drawSmith(ctx: CanvasRenderingContext2D, frame: number) {
    const L = this.L;
    const A = sprites();
    const { sx, sy } = L;
    const blink = frame % 40 === 0;
    A.draw(ctx, blink ? 'smithBlink' : 'smith', sx, sy);
    const pose = this.pose();
    const S = { x: sx + 11, y: sy + 14 };
    let hand: { x: number; y: number };
    let head: { x: number; y: number };
    let orient: 'h' | 'v' | 'd';
    const graduated = this.inputs.mode === 'graduated';
    switch (pose) {
      case 'raise':
        hand = { x: sx + 13, y: sy + 5 };
        head = { x: sx + 12, y: sy - 3 };
        orient = 'h';
        break;
      case 'mid':
        hand = { x: sx + 17, y: sy + 9 };
        head = { x: sx + 22, y: sy + 4 };
        orient = 'd';
        break;
      case 'strike': {
        const p = this.strikePoint;
        hand = { x: sx + 17, y: sy + 11 };
        head = { x: p.x, y: p.y - 3 };
        orient = 'v';
        break;
      }
      default:
        hand = { x: sx + 13, y: sy + 18 };
        head = graduated ? { x: sx + 13, y: sy + 25 } : { x: sx + 8, y: sy + 9 };
        orient = 'h';
    }
    // hammer head
    ctx.fillStyle = C.k;
    const hw = orient === 'h' ? 7 : orient === 'v' ? 5 : 5;
    const hh = orient === 'h' ? 5 : orient === 'v' ? 7 : 5;
    ctx.fillRect(head.x - (hw >> 1), head.y - (hh >> 1), hw, hh);
    ctx.fillStyle = C.iron;
    ctx.fillRect(head.x - (hw >> 1) + 1, head.y - (hh >> 1) + 1, hw - 2, hh - 2);
    ctx.fillStyle = C.ironHi;
    ctx.fillRect(head.x - (hw >> 1) + 1, head.y - (hh >> 1) + 1, hw - 2, 1);
    if (orient === 'v') {
      ctx.fillStyle = C.steel;
      ctx.fillRect(head.x - 1, head.y + (hh >> 1) - 1, 3, 1);
    }
    // handle
    ctx.fillStyle = C.k;
    line(ctx, hand.x, hand.y + 1, head.x, head.y + 1, 1);
    ctx.fillStyle = C.wood;
    line(ctx, hand.x, hand.y, head.x, head.y, 1);
    // arm
    ctx.fillStyle = C.k;
    line(ctx, S.x, S.y, hand.x, hand.y, 3);
    ctx.fillStyle = C.skin;
    line(ctx, S.x, S.y, hand.x, hand.y, 1);
    ctx.fillStyle = '#3B3358';
    ctx.fillRect(S.x - 1, S.y - 1, 2, 2);
    // fist
    ctx.fillStyle = C.k;
    ctx.fillRect(hand.x - 2, hand.y - 2, 4, 4);
    ctx.fillStyle = C.skin;
    ctx.fillRect(hand.x - 1, hand.y - 1, 2, 2);
    // a white-hot glint on the striking face
    if (pose === 'strike') {
      ctx.fillStyle = C.white;
      ctx.fillRect(head.x - 3, head.y + 3, 1, 1);
      ctx.fillRect(head.x + 3, head.y + 3, 1, 1);
    }
  }

  private drawFlights(ctx: CanvasRenderingContext2D, frame: number) {
    const L = this.L;
    for (const f of this.flights) {
      const sx = L.ax + 7;
      const sy = L.ay - 4;
      let x = sx;
      let y = sy;
      if (f.f >= FLIGHT_SIT) {
        const t = (f.f - FLIGHT_SIT + 1) / FLIGHT_LEN;
        const col = this.column(f.lot);
        const tx = col.x + (col.w >> 1) - 4;
        const ty = col.top - 4;
        x = Math.round(sx + (tx - sx) * t);
        y = Math.round(sy + (ty - sy) * t - 18 * 4 * t * (1 - t));
      }
      // glow halo
      ctx.fillStyle = C.molten;
      dither(ctx, x - 2, y - 2, 12, 8, frame);
      ingot(ctx, x, y, f.f < FLIGHT_SIT ? C.white : mix(C.white, f.color, (f.f - FLIGHT_SIT) / (FLIGHT_LEN * 2)));
    }
  }

  private drawPour(ctx: CanvasRenderingContext2D, frame: number) {
    const L = this.L;
    const p = Math.max(0, this.pour) / POUR_FRAMES;
    const cw = 34;
    const cx = L.ax - 3;
    const ch = 16;
    const cy = L.floorY - ch + 1;
    // crucible (trapezoid iron pot)
    for (let r = 0; r < ch; r++) {
      const inset = Math.floor((r * 4) / ch);
      ctx.fillStyle = C.k;
      ctx.fillRect(cx + inset, cy + r, cw - 2 * inset, 1);
      if (r > 0 && r < ch - 1) {
        ctx.fillStyle = r < 3 ? C.ironHi : C.crucible;
        ctx.fillRect(cx + inset + 1, cy + r, cw - 2 * inset - 2, 1);
      }
    }
    // rim
    ctx.fillStyle = C.k;
    ctx.fillRect(cx - 2, cy - 1, cw + 4, 3);
    ctx.fillStyle = C.ironHi;
    ctx.fillRect(cx - 1, cy, cw + 2, 1);
    // molten surface rises with the pour, then idles and bubbles
    const fill = Math.min(1, Math.max(0, (p - 0.15) / 0.7));
    if (fill > 0) {
      ctx.fillStyle = C.molten;
      ctx.fillRect(cx + 1, cy + 1, cw - 2, 1);
      for (let x = cx + 1; x < cx + cw - 1; x++) {
        const n = noise(x, Math.floor(frame / 2), 4);
        ctx.fillStyle = n > 0.85 ? C.white : n > 0.4 ? C.molten : C.ember;
        ctx.fillRect(x, cy + 1, 1, 1);
      }
      // glow above the pool
      ctx.fillStyle = mix(C.wall, C.molten, 0.35 * fill);
      dither(ctx, cx + 2, cy - 4, cw - 4, 3, frame);
    }
    // the ladle pours from above until the pool is full
    if (p < 1) {
      const lx = cx + cw / 2 + 2;
      const drop = Math.min(1, p * 6);
      const ly = Math.round(-10 + 22 * drop);
      ctx.fillStyle = C.iron;
      for (let y = 0; y < ly; y += 2) ctx.fillRect(lx + 7, y, 1, 1);
      // the ladle bowl, tipped toward its spout
      ctx.fillStyle = C.k;
      ctx.fillRect(lx - 5, ly, 13, 2);
      ctx.fillRect(lx - 4, ly + 2, 11, 2);
      ctx.fillRect(lx - 3, ly + 4, 9, 2);
      ctx.fillRect(lx - 8, ly - 1, 4, 3);
      ctx.fillStyle = C.crucible;
      ctx.fillRect(lx - 3, ly + 2, 9, 2);
      ctx.fillRect(lx - 2, ly + 4, 7, 1);
      ctx.fillStyle = C.molten;
      ctx.fillRect(lx - 4, ly + 1, 11, 1);
      ctx.fillRect(lx - 7, ly, 2, 1);
      if (p > 0.15 && p < 0.9) {
        // the stream
        for (let y = ly + 3; y < cy + 1; y++) {
          ctx.fillStyle = (y + frame) % 3 === 0 ? C.white : C.molten;
          ctx.fillRect(lx - 7, y, 2, 1);
        }
        this.sparkle(ctx, lx - 6, cy, frame);
      }
    }
    // the label
    ctx.fillStyle = C.molten;
    const label = 'UNISWAP POOL';
    drawText(ctx, label, Math.round(cx + cw / 2 - textWidth(label) / 2), L.floorY + 4);
  }

  private sparkle(ctx: CanvasRenderingContext2D, x: number, y: number, frame: number) {
    for (let i = 0; i < 4; i++) {
      const n = noise(i, frame);
      ctx.fillStyle = n > 0.5 ? C.white : C.molten;
      ctx.fillRect(x + Math.round((n - 0.5) * 10), y - 1 - Math.round(noise(i, frame, 2) * 4), 1, 1);
    }
  }
}

function ingotColumn(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color: string) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, h);
  if (h >= 3) {
    ctx.fillStyle = mix(color, '#FFFFFF', 0.45);
    ctx.fillRect(x, y, w, 1);
    ctx.fillStyle = mix(color, '#000000', 0.4);
    ctx.fillRect(x, y + h - 1, w, 1);
  }
}

/** an 8x4 ingot */
function ingot(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
  ctx.fillStyle = C.k;
  ctx.fillRect(x + 1, y, 6, 1);
  ctx.fillRect(x, y + 1, 8, 3);
  ctx.fillStyle = mix(color, '#FFFFFF', 0.5);
  ctx.fillRect(x + 1, y + 1, 6, 1);
  ctx.fillStyle = color;
  ctx.fillRect(x + 1, y + 2, 6, 1);
}
